import { AnnotationMetadata } from '@/components/AnnotationMetadata';
import { ClearIcon, IconButton } from '@/components/IconButton';
import { setAnnotationMeasurements, useExplore } from '@/stores/explore';
import { Layer } from '@/stores/viewer';
import { getLayerIcon } from '@/utils/nodeIcons';
import { truncateText } from '@/utils/truncateText';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import {
  chip,
  chipIcon,
  cn,
  Input,
  ProgressButton,
  Select,
  Toast,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@skand/ui';

import {
  areaToString,
  calculateEdgeLengths,
  calculatePolygonArea,
  Cartographic,
  lengthToString,
  VolumetricBaseMode,
  Volumetrics,
} from '@skand/viewer-component-v2';
import { debounce } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { Vector3 } from 'three';
import { RemoveCalculationModal } from '../RemoveCalculationModal';
import { Item } from './Item';
import { LayerSelector } from './LayerSelector';
import { Point } from './Point';
import { SectioningView } from './SectioningView';
import { Segments } from './Segments';
import { VolumetricsView } from './VolumetricsView';

export const VOLUMETRICS_BASE_OPTIONS = [
  {
    key: 'triangulated',
    name: 'Triangulated',
  },
  {
    key: 'smart',
    name: 'Best fit',
  },
  {
    key: 'min',
    name: 'Lowest',
  },
  {
    key: 'max',
    name: 'Highest',
  },
  {
    key: 'custom',
    name: 'Custom',
  },
] as const;
export type VolumetricBaseOption = (typeof VOLUMETRICS_BASE_OPTIONS)[number]['key'];

interface AnnotationMeasurementsProps {
  points: Vector3[];
  shapeType: 'Polygon' | 'Polyline' | 'Point' | undefined;
  baseSurface: VolumetricBaseOption;
  baseHeight: number;
  selectedLayer: Layer | null;
  sections: Cartographic[][] | null;
  volumetrics: Volumetrics | null;
  setBaseSurface: (value: VolumetricBaseOption) => void;
  setBaseHeight: (value: number) => void;
  setSelectedLayer: (value: Layer | null) => void;
  setSections: (value: Cartographic[][] | null) => void;
  setVolumetrics: (value: Volumetrics | null) => void;
  computeVolumetrics: (layer: Layer, baseMode: VolumetricBaseMode, isCreate: boolean) => void;
  computeSectioning: (layer: Layer, isCreate: boolean) => void;
  isLoading: boolean;
  loadingProgress: number;
  loadingStage: string;
}

export const AnnotationMeasurements = ({
  points,
  shapeType,
  baseHeight,
  baseSurface,
  selectedLayer,
  sections,
  volumetrics,
  isLoading,
  setBaseSurface,
  setBaseHeight,
  setSelectedLayer,
  computeSectioning,
  computeVolumetrics,
  loadingProgress,
  loadingStage,
}: AnnotationMeasurementsProps) => {
  const measurementUnit = useExplore(state => state.measurementUnit);
  const annotationDraft = useExplore(state => state.annotationDraft);
  const annotationOriginalSectionsCache = useExplore(
    state => state.annotationOriginalSectionsCache,
  );
  const annotationOriginalVolumetricsCache = useExplore(
    state => state.annotationOriginalVolumetricsCache,
  );

  const [enabledLayerSelector, setEnabledLayerSelector] = useState(false);
  const [enabledPoints, setEnabledPoints] = useState(false);
  const [enabledRemoveCalculationModal, setEnabledRemoveCalculationModal] = useState(false);
  const [enabledLargeModalInfoTip, setEnabledLargeModalInfoTip] = useState(false);

  const enabledClearButton = volumetrics || sections;

  const area = useMemo(() => calculatePolygonArea(points), [points]);

  const totalLength = useMemo(() => {
    const lengths = calculateEdgeLengths(points, shapeType === 'Polygon');
    return lengths.reduce((prev, curr) => prev + curr, 0);
  }, [shapeType, points]);

  // Get selected layer details
  const [selectedLayerName, selectedLayerIcon] = useMemo(() => {
    const layerName = selectedLayer?.name ?? 'Select Layer';
    const layerIcon = selectedLayer?.formatType ?? null;
    return [layerName, layerIcon];
  }, [selectedLayer]);

  const measurementSubTitle = useMemo(() => {
    switch (shapeType) {
      case 'Polyline':
        return 'Generate elevation profile';
      case 'Polygon':
        return 'Calculate volumetric';
    }
  }, [shapeType]);

  const enabledEmptyMeasurementAlert = useMemo(() => {
    if (shapeType === 'Polygon' && volumetrics && Math.abs(volumetrics.surfaceArea) < 0.001) {
      return true;
    } else if (shapeType === 'Polyline' && sections?.every(section => section.length === 0)) {
      return true;
    }
    return false;
  }, [shapeType, volumetrics, sections]);

  const volumetricsView = useMemo(() => {
    // If annotationOriginalVolumetricsCache is available, load view using annotationOriginalVolumetricsCache instead of annotationDraft
    if (annotationOriginalVolumetricsCache) {
      return <VolumetricsView volumetrics={annotationOriginalVolumetricsCache} />;
    } else if (!isLoading && volumetrics && shapeType === 'Polygon')
      return <VolumetricsView volumetrics={volumetrics} />;
  }, [annotationOriginalVolumetricsCache, isLoading, volumetrics, shapeType]);

  const sectioningView = useMemo(() => {
    // If annotationOriginalSectionsCache is available, load view using annotationOriginalSectionsCache instead of annotationDraft
    if (annotationOriginalSectionsCache) {
      return <SectioningView sections={annotationOriginalSectionsCache} />;
    } else if (!isLoading && sections && shapeType === 'Polyline')
      return <SectioningView sections={sections} />;
  }, [annotationOriginalSectionsCache, isLoading, sections, shapeType]);

  const handleGenerate = () => {
    if (selectedLayer) {
      computeSectioning(selectedLayer, true);
    }
  };

  const handleCalculate = () => {
    if (selectedLayer) {
      computeVolumetrics(
        selectedLayer,
        baseSurface === 'custom' ? baseHeight : (baseSurface as VolumetricBaseMode),
        true,
      );
    }
  };

  // Handle opening remove calculation modal
  const handleOpenRemoveCalculationModal = () => {
    setEnabledRemoveCalculationModal(true);
  };

  // Handle closing remove calculation modal
  const handleCloseRemoveCalculationModal = () => {
    setEnabledRemoveCalculationModal(false);
  };

  // Remove all calculations
  const handleConfirmRemoveCalculation = () => {
    let measurements;
    if (shapeType === 'Polygon') {
      measurements = {
        baseSurface: null,
        cutVolume: null,
        fillVolume: null,
        layerId: null,
        maxElevation: null,
        minElevation: null,
        projectedArea: null,
        surfaceArea: null,
      };
    } else {
      measurements = {
        hasSectioning: false,
        layerId: null,
      };
    }
    if (annotationDraft) annotationDraft.sketch3D?.show();
    setAnnotationMeasurements(measurements);
    setEnabledRemoveCalculationModal(false);
  };

  // Handle selecting the base surface for volumetrics
  const handleSelectBaseSurface = (item: VolumetricBaseOption) => {
    setBaseSurface(item);
    useExplore.setState({ annotationBaseSurfaceCache: item });
    if (shapeType === 'Polygon' && selectedLayer && volumetrics) {
      computeVolumetrics(
        selectedLayer,
        item === 'custom' ? baseHeight : (item as VolumetricBaseMode),
        true,
      );
    }
  };

  const debouncedSetBaseHeight = useMemo(
    () =>
      debounce((value: number) => {
        setBaseHeight(value);
        if (shapeType === 'Polygon' && selectedLayer && volumetrics && value) {
          computeVolumetrics(selectedLayer, value, true);
        }
      }, 300),
    [computeVolumetrics, selectedLayer, setBaseHeight, shapeType, volumetrics],
  );

  // Handle changing the custom base height for volumetrics
  const handleChangeBaseHeight = (height: number) => {
    if (isNaN(height)) {
      debouncedSetBaseHeight(height);
    } else {
      debouncedSetBaseHeight(height);
    }
  };

  // Handle selecting a layer
  const handleSelectLayer = (layer: Layer) => {
    setSelectedLayer(layer);
    useExplore.setState({ annotationLayerCache: layer });
    if (shapeType === 'Polygon' && baseSurface && volumetrics) {
      computeVolumetrics(
        layer,
        baseSurface === 'custom' ? baseHeight : (baseSurface as VolumetricBaseMode),
        true,
      );
    }
    if (shapeType === 'Polyline' && sections) {
      computeSectioning(layer, true);
    }
  };

  useEffect(() => {
    return () => {
      debouncedSetBaseHeight.cancel();
    };
  }, [debouncedSetBaseHeight]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      isLoading && setEnabledLargeModalInfoTip(true);
    }, 1000);

    !isLoading && setEnabledLargeModalInfoTip(false);

    return () => clearTimeout(timeout);
  }, [isLoading]);

  return (
    <div
      className={cn(
        'min-h-10vh',
        'max-h-60vh',
        'overflow-auto',
        'border-solid',
        'border-t-neutral-300',
        'border-[1px]',
      )}
    >
      <p className="pointer-events-none mt-3 text-neutral-800 typo-text-m-em">{shapeType}</p>
      {shapeType === 'Point' ? (
        <div className="skand-rnd-disable px-1">
          <Point position={points[0]} />
        </div>
      ) : (
        <>
          <div className="skand-rnd-disable px-1">
            {shapeType === 'Polyline' && <Segments points={points} />}
            <Item
              label={shapeType === 'Polygon' ? 'Perimeter' : 'Total length'}
              value={lengthToString(totalLength, measurementUnit, 4)}
            />
            {shapeType === 'Polygon' && area && (
              <Item label="Area" value={areaToString(area, measurementUnit, 4)} />
            )}
          </div>

          <div className="mt-3 flex flex-row items-center justify-between border-t-1 border-t-neutral-300 border-t-solid pt-3">
            <p className="pointer-events-none text-neutral-800 typo-text-m-em">
              {measurementSubTitle}
            </p>
            {enabledClearButton && (
              <IconButton
                buttonIcon={<ClearIcon />}
                buttonState="default"
                className={cn('bg-neutral-100 cursor-pointer')}
                onClick={handleOpenRemoveCalculationModal}
              />
            )}
          </div>
          {enabledLargeModalInfoTip && (
            <Toast
              clickToDismiss={false}
              message="Larger models may take longer to calculate."
              props={{
                className: 'mt-3 shadow-none',
              }}
              size="l"
              type="info"
            />
          )}

          {shapeType === 'Polygon' && (
            <>
              <div className="mb-2 mt-3 flex flex-row items-center">
                <p className="pointer-events-none text-neutral-600 typo-text-s">Base surface</p>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <span
                      className={cn(
                        'i-skand-info',
                        'color-neutral-500',
                        'ml-2',
                        'text-3.5',
                        'cursor-pointer',
                      )}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className={chip({ intent: 'info' })}>
                      <div className={cn('flex flex-row items-center gap2 py-1')}>
                        <span
                          className={cn(
                            'i-skand-info',
                            'color-info-400',
                            'ml-2',
                            'text-3.5',
                            'cursor-pointer',
                          )}
                        />
                        <div className={cn('flex flex-col items-start')}>
                          <p className={cn('text-neutral-800 typo-text-s')}>
                            <strong>Triangulated</strong> - A triangulated mesh of the polygon
                            points.
                          </p>
                          <p className={cn('text-neutral-800 typo-text-s')}>
                            <strong>Best fit</strong> - A plane of best fit for all the polygon
                            points.
                          </p>
                          <p className={cn('text-neutral-800 typo-text-s')}>
                            <strong>Lowest</strong> - A plane parallel to ground at the lowest
                            polygon point.
                          </p>
                          <p className={cn('text-neutral-800 typo-text-s')}>
                            <strong>Highest</strong> - A plane parallel to ground at the highest
                            polygon point.
                          </p>
                          <p className={cn('text-neutral-800 typo-text-s')}>
                            <strong>Custom</strong> - A plane parallel to ground with a custom
                            height level.
                          </p>
                        </div>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Select
                onChange={handleSelectBaseSurface}
                options={[...VOLUMETRICS_BASE_OPTIONS]}
                style={{ display: 'block' }}
                value={baseSurface}
                width="full"
              />
              {baseSurface === 'custom' && (
                <div className="mt-3">
                  <Input
                    onChange={e => handleChangeBaseHeight(parseFloat(e))}
                    placeholder="Specify height in meters"
                    value={baseHeight.toString()}
                  />
                </div>
              )}
            </>
          )}
          <div className="mb-2 mt-3 flex flex-row items-center">
            <p className="pointer-events-none text-neutral-600 typo-text-s">
              Measurement reference
            </p>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    'i-skand-info',
                    'color-neutral-500',
                    'ml-2',
                    'text-3.5',
                    'cursor-pointer',
                  )}
                />
              </TooltipTrigger>
              <TooltipContent>
                <div className={chip({ intent: 'info' })}>
                  <div className={chipIcon({ intent: 'info' })} />
                  Supported layer types include 3D Mesh, IFC, and OBJs (no terrain).
                </div>
              </TooltipContent>
            </Tooltip>
          </div>

          <DropdownMenu.Root
            modal={false}
            onOpenChange={setEnabledLayerSelector}
            open={enabledLayerSelector}
          >
            <DropdownMenu.Trigger asChild>
              <div
                className={cn(
                  'mt-2',
                  'flex',
                  'flex-row',
                  'items-center',
                  'justify-between',
                  'rounded-1',
                  'border-neutral-400',
                  'border-1',
                  'border-solid',
                  'h-9',
                  'px-3',
                  'cursor-pointer',
                )}
              >
                <div className="flex flex-1 items-center gap-1">
                  {selectedLayerIcon && (
                    <div
                      className={cn(
                        'w-5 color-neutral-600 text-3 flex-none',
                        getLayerIcon(selectedLayerIcon),
                      )}
                    />
                  )}

                  <p className={cn('typo-text-s', 'color-neutral-600')}>
                    {truncateText(selectedLayerName, 45)}
                  </p>
                </div>
                <div className={cn('i-skand-dropdown', 'color-neutral-400', 'text-3')} />
              </div>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content align="center" className="z-5" side="bottom">
                <LayerSelector
                  onClose={() => setEnabledLayerSelector(false)}
                  onSelect={handleSelectLayer}
                  selectedLayer={selectedLayer}
                />
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
          {enabledEmptyMeasurementAlert && (
            <div
              className={cn(
                'w-full',
                'mt-3',
                'p-2',
                'mb-3',
                'bg-warning-100',
                'border',
                'border-warning-400',
                'border-solid',
                'rounded',
                'flex',
                'flex-row',
                'pointer-events-none',
              )}
            >
              <div className="flex items-center gap-[12px]">
                <div className="i-skand-warn ml-2 text-[24px] color-warning-400" />
                <div>
                  <p className="color-neutral-800 typo-text-s">
                    The selected layer is not overlapping with the current annotation. Calculations
                    may be inaccurate.
                  </p>
                </div>
              </div>
            </div>
          )}

          {volumetricsView}
          {sectioningView}

          {((!volumetrics && !sections) || isLoading) && (
            <ProgressButton
              className={cn(
                'mt-3',
                'active',
                'h-7',
                'w-full',
                'flex',
                'items-center',
                'justify-center',
                'rounded-xs',
                isLoading && 'pointer-events-none',
              )}
              disabled={!selectedLayer}
              filled={false}
              onClick={shapeType === 'Polyline' ? handleGenerate : handleCalculate}
              progress={loadingProgress}
              size="small"
            >
              {isLoading ? loadingStage : shapeType === 'Polyline' ? 'Generate' : 'Calculate'}
            </ProgressButton>
          )}
          <div
            className="mt-3 flex flex-row cursor-pointer items-center gap-2 border-t-1 border-t-neutral-400 border-t-solid pt-3"
            onClick={() => setEnabledPoints(!enabledPoints)}
          >
            <div
              className={cn(
                'i-skand-dropdown text-14px color-neutral-400 transition-transform',
                enabledPoints ? 'rotate-180' : 'rotate-270',
              )}
            />
            <p className="text-neutral-600 typo-text-s-em">
              {enabledPoints ? 'Hide' : 'Show'} points
            </p>
          </div>
        </>
      )}
      <div className="skand-rnd-disable">
        {enabledPoints && shapeType !== 'Point' && (
          <AnnotationMetadata
            hideObjectDetails
            isClosed={shapeType === 'Polygon'}
            points={points}
          />
        )}
      </div>

      <RemoveCalculationModal
        onCancel={handleCloseRemoveCalculationModal}
        onSubmit={handleConfirmRemoveCalculation}
        open={enabledRemoveCalculationModal}
        shapeType={shapeType}
      />
    </div>
  );
};
